import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import PyrolysisDiagram from "@/components/PyrolysisDiagram";
import { usePageMeta } from "@/hooks/usePageMeta";

// Section anchor slugs (used in both the ToC and section IDs).
const SECTION_SLUG = {
  "The Basics": "the-basics",
  "Carbon & Climate": "carbon-climate",
  "Certification & Standards": "certification-standards",
  "Engineering & Project Terms": "engineering-project-terms",
};

const SECTIONS = [
  {
    number: "01",
    title: "The Basics",
    terms: [
      {
        slug: "pyrolysis",
        term: "Pyrolysis",
        direct: "Pyrolysis is the thermal decomposition of organic material at high temperature without oxygen.",
        def: "Unlike burning, which needs oxygen and just releases CO\u2082, pyrolysis breaks biomass down into three outputs: a solid (biochar), a liquid (bio-oil), and a gas (syngas).",
        why: "At Tanren, pyrolysis is the core reactor technology we design, build and operate — because the difference between a spec-sheet pyrolysis system and one that actually holds up at 24 TPD is engineering discipline the team has already lived through.",
        diagram: true,
      },
      {
        slug: "biochar",
        term: "Biochar",
        direct: "Biochar is the solid, carbon-rich material left after pyrolysis.",
        def: "Its defining feature is stability \u2014 the carbon in biochar resists breaking back down into CO\u2082 for centuries, which is why it works both as a soil amendment and as a durable way to remove carbon from the atmosphere.",
        why: "Tanren systems are engineered to produce EBC-AgroOrganic grade biochar, suitable for organic agriculture and carbon-removal credit registries.",
      },
      {
        slug: "bio-oil",
        term: "Bio-Oil",
        direct: "Bio-oil is the liquid output of pyrolysis, formed when vapors released during the process are rapidly cooled and condensed.",
        def: "It can be refined into fuels or chemicals, or used directly as an industrial fuel replacement.",
        why: "Tanren works on end-use application engineering for bio-oil so a facility doesn't produce a liquid it can't sell \u2014 industrial fuel replacement, distillation to specialty chemicals, or building materials support are all validated pathways.",
      },
      {
        slug: "syngas",
        term: "Syngas",
        direct: "Syngas is the gaseous output of pyrolysis \u2014 a mix of hydrogen, carbon monoxide, and other gases.",
        def: "It is often used to power the process itself.",
      },
    ],
  },
  {
    number: "02",
    title: "Carbon & Climate",
    terms: [
      {
        slug: "cdr",
        term: "CDR (Carbon Dioxide Removal)",
        def: "Any process that actively removes CO\u2082 already in the atmosphere and stores it durably. Biochar is one of the few CDR methods commercially deployable at scale today.",
        why: "Biochar sits at the intersection of the fastest-scaling CDR pathways \u2014 which is why Tanren treats every plant we engineer as a CDR project from day one, not an afterthought.",
      },
      {
        slug: "carbon-credit",
        term: "Carbon Credit",
        def: "A tradeable certificate representing one tonne of CO\u2082 removed or avoided, generated once carbon removal is independently verified.",
      },
      {
        slug: "mrv",
        term: "MRV (Measurement, Reporting, and Verification)",
        def: "The process of proving a carbon removal claim is real \u2014 measuring how much carbon was captured, reporting it transparently, and having a third party verify the numbers.",
        why: "Tanren designs MRV frameworks and field-sampling protocols into the plant itself, so every tonne of carbon claimed is a tonne that stands up in an audit.",
      },
      {
        slug: "lca",
        term: "LCA (Life Cycle Assessment)",
        def: "A full accounting of a product's environmental impact across its entire life, confirming a project is a genuine net-positive once every input is accounted for.",
      },
      {
        slug: "additionality",
        term: "Additionality",
        def: "Proof that a carbon removal project wouldn't have happened anyway without it \u2014 a core requirement for issuing carbon credits.",
      },
    ],
  },
  {
    number: "03",
    title: "Certification & Standards",
    terms: [
      {
        slug: "ebc",
        term: "EBC (European Biochar Certificate)",
        def: "A widely recognized quality and safety standard for biochar, covering feedstock, process, and contaminant limits. Includes a \u201CFeed\u201D category certified safe for animal feed use.",
        why: "Our TP 24 system is engineered to produce EBC-AgroOrganic grade biochar, so operators can access premium markets from commissioning day.",
      },
      {
        slug: "iscc",
        term: "ISCC (International Sustainability & Carbon Certification)",
        def: "A global certification proving biomass and biofuels meet sustainability requirements, often required for EU markets.",
      },
      {
        slug: "registries",
        term: "Puro.earth / Gold Standard / Verra",
        def: "Independent carbon credit registries that verify removal claims and issue tradeable credits once a project meets their methodology requirements.",
      },
    ],
  },
  {
    number: "04",
    title: "Engineering & Project Terms",
    terms: [
      { slug: "feed", term: "FEED (Front-End Engineering Design)", def: "Detailed technical planning before construction, defining how a plant will be built and sized." },
      { slug: "pid", term: "P&ID (Piping and Instrumentation Diagram)", def: "A detailed drawing showing every pipe, valve, and control system in a plant." },
      { slug: "pfd-ufd", term: "PFD / UFD (Process / Utility Flow Diagram)", def: "High-level diagrams of main process steps and supporting utilities like steam and power." },
      { slug: "hmbd", term: "HMBD (Heat & Mass Balance Diagram)", def: "Tracks material and energy in and out of a plant at every stage, confirming a design will actually perform as expected." },
      { slug: "tea-teea", term: "TEA / TEEA (Techno-Economic Assessment)", def: "Evaluates whether a project makes financial sense; TEEA extends this into environmental and social factors." },
      { slug: "dpr-pdd", term: "DPR / PDD (Detailed Project Report / Project Design Document)", def: "The comprehensive document laying out a project's technical, financial, and carbon methodology details." },
      { slug: "capex-opex", term: "CAPEX / OPEX", def: "Upfront cost to build a plant vs. ongoing cost to run it." },
      { slug: "hazop", term: "HAZOP (Hazard and Operability Study)", def: "A structured safety review identifying potential hazards before construction." },
      { slug: "atfe", term: "ATFE (Agitated Thin Film Evaporator)", def: "Equipment used to purify liquids like bio-oil via thin-film evaporation." },
      { slug: "plc-scada", term: "PLC / SCADA", def: "PLC is the hardware that automates plant operations; SCADA is the software letting operators monitor and control it remotely." },
    ],
  },
];

// FAQPage entries phrase the same core terms as questions, with slightly varied wording
// from the DefinedTerm bodies so the two schema blocks don't duplicate verbatim.
const FAQ_ENTRIES = [
  {
    q: "What is pyrolysis?",
    a: "Pyrolysis is heating biomass in an oxygen-free chamber so it breaks down into three products — a solid biochar, a liquid bio-oil, and a syngas — rather than combusting and releasing CO2.",
  },
  {
    q: "What is biochar?",
    a: "Biochar is a stable, carbon-rich charcoal produced by pyrolysis; because its carbon locks in for centuries, it is used both to improve soils and to generate durable carbon-removal credits.",
  },
  {
    q: "What is bio-oil?",
    a: "Bio-oil is the condensed liquid captured from pyrolysis vapours. It can substitute for industrial fuel oil, or be refined further into chemicals and specialty products.",
  },
  {
    q: "What does MRV mean?",
    a: "MRV — Measurement, Reporting and Verification — is the audit chain that turns a carbon-removal claim into a tradeable credit by making the numbers measurable, transparent and independently checked.",
  },
  {
    q: "What is CDR (Carbon Dioxide Removal)?",
    a: "CDR is any method that pulls existing CO2 out of the atmosphere and stores it durably. Biochar is one of a small number of CDR pathways operating at commercial scale today.",
  },
  {
    q: "What is the EBC?",
    a: "The European Biochar Certificate is a quality and safety standard for biochar. Its top tier, EBC Feed, certifies material clean enough to be used in animal feed.",
  },
];

const Learn = () => {
  usePageMeta(
    "Biochar, Pyrolysis & Carbon Market Terms Explained | Tanren",
    "What is pyrolysis? What is biochar? Clear definitions of the terms behind biochar, bio-oil, carbon removal, and pyrolysis engineering."
  );

  // Inject two JSON-LD blocks (DefinedTermSet + FAQPage) into <head>.
  useEffect(() => {
    const definedTermSet = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Tanren Biochar, Bio-Oil & Carbon Market Glossary",
      hasDefinedTerm: SECTIONS.flatMap((s) =>
        s.terms.map((t) => ({
          "@type": "DefinedTerm",
          "@id": `https://tanrentech.com/learn#${t.slug}`,
          name: t.term,
          description: [t.direct, t.def].filter(Boolean).join(" "),
          inDefinedTermSet: "https://tanrentech.com/learn",
          termCode: t.slug,
        }))
      ),
    };

    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ENTRIES.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.dataset.jsonld = "learn-defined-terms";
    s1.textContent = JSON.stringify(definedTermSet);

    const s2 = document.createElement("script");
    s2.type = "application/ld+json";
    s2.dataset.jsonld = "learn-faq";
    s2.textContent = JSON.stringify(faqPage);

    document.head.appendChild(s1);
    document.head.appendChild(s2);
    return () => {
      s1.remove();
      s2.remove();
    };
  }, []);

  // If we arrive with a hash (e.g. /learn#hmbd), scroll it into view once mounted.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    // Slight delay to let sections render.
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="page-learn">
      {/* HERO */}
      <section
        data-testid="learn-hero"
        className="bg-paper text-char-900 pt-40 pb-20 lg:pb-28 border-b border-char-900/10"
      >
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <h1 className="font-display mt-6 max-w-[20ch] text-char-900">
            The terms behind the technology.
          </h1>
          <p className="mt-8 max-w-[62ch] text-lg md:text-xl text-char-700 leading-relaxed">
            Biochar, pyrolysis, and carbon markets come with a lot of acronyms. Here's what they
            actually mean.
          </p>

          {/* Table of contents — jumps to section anchors */}
          <nav
            aria-label="Table of contents"
            data-testid="learn-toc"
            className="mt-16 grid md:grid-cols-2 md:gap-x-16 border-t border-char-900/10"
          >
            {SECTIONS.map((s) => (
              <a
                key={s.number}
                href={`#${SECTION_SLUG[s.title]}`}
                data-testid={`learn-toc-${s.number}`}
                className="group flex items-baseline gap-6 py-5 border-b border-char-900/10"
              >
                <span className="numeral text-xl w-8 shrink-0">{s.number}</span>
                <span className="font-display text-base tracking-tight text-char-900 leading-tight group-hover:text-ember transition-colors">
                  {s.title}
                </span>
                <ArrowUpRight
                  size={15}
                  aria-hidden="true"
                  className="ml-auto mt-1 shrink-0 text-char-700 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 divide-y divide-char-900/10">
          {SECTIONS.map((s, si) => (
            <ScrollReveal key={s.number} delay={si * 40}>
              <div
                id={SECTION_SLUG[s.title]}
                data-testid={`learn-section-${s.number}`}
                className="py-20 lg:py-28 grid md:grid-cols-12 gap-y-10 md:gap-x-16 scroll-mt-28"
              >
                <div className="md:col-span-4 md:sticky md:top-32 self-start">
                  <span aria-hidden="true" className="numeral text-5xl block">
                    {s.number}
                  </span>
                  <span className="block mt-5 w-10 h-px bg-char-900/20" aria-hidden="true" />
                  <h2 className="mt-5 font-display text-char-900">
                    {s.title}
                  </h2>
                </div>

                <div className="md:col-span-8 min-w-0">
                  <dl className="min-w-0">
                    {s.terms.map((t, ti) => (
                      <div
                        key={t.slug}
                        data-testid={`learn-term-${t.slug}`}
                        className={`grid md:grid-cols-8 gap-y-4 md:gap-x-10 scroll-mt-28 min-w-0 ${
                          ti === 0 ? "" : "mt-14 pt-14 border-t border-char-900/10"
                        }`}
                      >
                        <dt
                          id={t.slug}
                          className="md:col-span-3 min-w-0 font-display text-xl md:text-2xl tracking-tight text-char-900 scroll-mt-28"
                        >
                          {t.term}
                        </dt>
                        <dd className="md:col-span-5 min-w-0 text-char-700 text-[15.5px] md:text-base leading-relaxed max-w-[62ch] space-y-4">
                          {t.direct && (
                            <p className="text-char-900 font-medium">{t.direct}</p>
                          )}
                          <p>{t.def}</p>
                          {t.why && (
                            <div className="pt-2">
                              <span className="mono-label block">Tanren</span>
                              <p className="mt-2 text-char-700">{t.why}</p>
                            </div>
                          )}
                        </dd>
                        {t.diagram && (
                          <div className="md:col-span-8 min-w-0 pt-4">
                            <PyrolysisDiagram />
                          </div>
                        )}
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-char-900 text-paper py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display max-w-[28ch]">
            Have a term you don't see here? Ask us directly.
          </h2>
          <Link to="/contact" className="btn-ember shrink-0" data-testid="learn-cta">
            Start the Conversation
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Learn;
